import type { Call, Capability, EventEnvelope, Transcript } from '@bcio-voice/contracts';
import { isAbortError, type ApiClient, type DataEnvelope } from '../../lib/api.ts';
import { describeError, type ErrorView } from '../../lib/errorCopy.ts';
import type { MediaAccess } from '../../state/mediaAccess.ts';

export const LIVE_TRANSCRIPT_POLL_MS = 4000;

export function liveTranscriptVisible(
  cap: Capability | null | undefined,
  permissions: readonly string[],
  mediaReady: boolean,
): boolean {
  return !!cap?.allowed && (cap.status === 'enabled' || cap.status === 'degraded') && permissions.includes('transcripts:read') && mediaReady;
}

export type TranscriptStreamNotification =
  | { kind: 'event'; event: EventEnvelope }
  | { kind: 'resync' };

type Listener = (notification: TranscriptStreamNotification) => void;
const streamListeners = new Set<Listener>();

export function subscribeTranscriptStream(listener: Listener): () => void {
  streamListeners.add(listener);
  return () => {
    streamListeners.delete(listener);
  };
}

export function notifyTranscriptEvent(event: EventEnvelope): void {
  if (
    event.aggregate.type !== 'transcript_segment' &&
    event.aggregate.type !== 'transcript_gap' &&
    event.type !== 'transcript.state_changed'
  ) {
    return;
  }
  for (const listener of [...streamListeners]) {
    listener({ kind: 'event', event });
  }
}

export function notifyTranscriptResync(): void {
  for (const listener of [...streamListeners]) {
    listener({ kind: 'resync' });
  }
}

type Segment = Transcript['segments'][number];

export function readTranscriptSegment(raw: unknown): Segment | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (typeof s.id !== 'string' || !s.id) return null;
  if (s.participant_id !== null && typeof s.participant_id !== 'string') return null;
  if (typeof s.start_ms !== 'number' || !Number.isFinite(s.start_ms)) return null;
  if (typeof s.end_ms !== 'number' || !Number.isFinite(s.end_ms)) return null;
  if (typeof s.text !== 'string') return null;
  if (typeof s.revision !== 'number' || !Number.isInteger(s.revision) || s.revision < 1) return null;
  if (typeof s.is_final !== 'boolean') return null;
  if (s.source !== 'live' && s.source !== 'refined') return null;
  if (typeof s.model_id !== 'string') return null;
  const committed = readCommittedText(s.committed_text, s.text);
  return {
    id: s.id,
    participant_id: s.participant_id,
    start_ms: s.start_ms,
    end_ms: s.end_ms,
    text: s.text,
    ...(committed === null ? {} : { committed_text: committed }),
    revision: s.revision,
    is_final: s.is_final,
    source: s.source,
    model_id: s.model_id,
  };
}

export const COMMITTED_TEXT_MAX = 20000;

/** committed_text kept only when it holds the contract's invariant (a prefix of text, at most 20000 characters); an
 * older ASR release sends none, and a value that breaks the invariant is dropped as if it had not been sent. */
function readCommittedText(raw: unknown, text: string): string | null {
  if (typeof raw !== 'string' || raw.length > COMMITTED_TEXT_MAX || !text.startsWith(raw)) return null;
  return raw;
}

/**
 * The locked words of a segment (docs/design/CALL-POP-KRISP-BAR.md section 3): a final is locked whole; a partial is
 * locked up to its committed_text, and a partial without one (an older ASR release) is pending whole.
 */
export function committedPrefix(segment: Pick<Segment, 'text' | 'is_final' | 'committed_text'>): string {
  if (segment.is_final) return segment.text;
  return readCommittedText(segment.committed_text, segment.text) ?? '';
}

export function applyTranscriptSseEvent(
  current: Transcript | null,
  event: EventEnvelope,
  callId: string,
): { transcript: Transcript | null; shouldReload: boolean; isFinalSegment: boolean } {
  if (event.type === 'transcript.state_changed' && event.aggregate.id === callId) {
    const nextState = (event.data as { state?: unknown } | undefined)?.state;
    const updated =
      current &&
      (nextState === 'off' ||
        nextState === 'pending' ||
        nextState === 'live' ||
        nextState === 'delayed' ||
        nextState === 'complete' ||
        nextState === 'failed')
        ? nextState === 'off'
          ? { state: 'off' as const, segments: [], gaps: [], next_cursor: null }
          : { ...current, state: nextState }
        : current;
    return { transcript: updated, shouldReload: true, isFinalSegment: false };
  }
  if (event.aggregate.type === 'transcript_segment') {
    const data = event.data as { call_id?: unknown; segment?: unknown } | undefined;
    if (!data || data.call_id !== callId) {
      return { transcript: current, shouldReload: false, isFinalSegment: false };
    }
    const seg = readTranscriptSegment(data.segment);
    if (!seg) {
      return { transcript: current, shouldReload: true, isFinalSegment: false };
    }
    const base: Transcript = current ?? { state: 'live', segments: [], gaps: [], next_cursor: null };
    const idx = base.segments.findIndex(s => s.id === seg.id);
    if (idx >= 0 && base.segments[idx]!.revision > seg.revision) {
      return { transcript: base, shouldReload: false, isFinalSegment: false };
    }
    const segments = idx >= 0
      ? base.segments.map((s, i) => (i === idx ? seg : s))
      : [...base.segments, seg];
    segments.sort((a, b) => a.start_ms - b.start_ms || a.id.localeCompare(b.id));
    return {
      transcript: {
        ...base,
        state: base.state === 'pending' ? 'live' : base.state,
        segments,
      },
      shouldReload: false,
      isFinalSegment: seg.is_final,
    };
  }
  if (event.aggregate.type === 'transcript_gap') {
    const data = event.data as { call_id?: unknown; start_ms?: unknown; end_ms?: unknown; reason?: unknown } | undefined;
    if (!data || data.call_id !== callId) {
      return { transcript: current, shouldReload: false, isFinalSegment: false };
    }
    if (typeof data.start_ms !== 'number' || typeof data.end_ms !== 'number' || typeof data.reason !== 'string') {
      return { transcript: current, shouldReload: true, isFinalSegment: false };
    }
    const base: Transcript = current ?? { state: 'live', segments: [], gaps: [], next_cursor: null };
    const exists = base.gaps.some(g => g.start_ms === data.start_ms && g.end_ms === data.end_ms && g.reason === data.reason);
    const gaps = exists
      ? base.gaps
      : [...base.gaps, { start_ms: data.start_ms, end_ms: data.end_ms, reason: data.reason }].sort((a, b) => a.start_ms - b.start_ms);
    return { transcript: { ...base, gaps }, shouldReload: false, isFinalSegment: false };
  }
  return { transcript: current, shouldReload: false, isFinalSegment: false };
}

function mergeFetchedTranscript(
  current: Transcript | null,
  fetched: Transcript,
  inFlightEvents: readonly EventEnvelope[],
  callId: string,
): Transcript {
  if (fetched.state === 'off') {
    return { state: 'off', segments: [], gaps: [], next_cursor: null };
  }
  let merged: Transcript = fetched;
  if (current && current.segments.length > 0) {
    const currentById = new Map(current.segments.map(s => [s.id, s]));
    const segments = merged.segments.map(s => {
      const prior = currentById.get(s.id);
      // The same revision from the stream may carry the locked prefix the transcript route leaves out: keep it, so a
      // fallback poll never turns locked words back into pending ones.
      if (prior && prior.revision === s.revision && prior.committed_text !== undefined && s.committed_text === undefined && prior.text === s.text) return prior;
      return prior && prior.revision > s.revision ? prior : s;
    });
    segments.sort((a, b) => a.start_ms - b.start_ms || a.id.localeCompare(b.id));
    merged = { ...merged, segments };
  }
  for (const ev of inFlightEvents) {
    merged = applyTranscriptSseEvent(merged, ev, callId).transcript ?? merged;
  }
  return merged;
}

/** The transcript route returns the oldest 100 segments a page; a long call follows next_cursor so its newest lines
 * are not dropped on every poll. At most this many pages per poll. */
export const TRANSCRIPT_MAX_PAGES = 10;

/**
 * GET /calls/{id}/transcript, every page. Gaps and state come from the first page (the route sends them all on each
 * page). `stillCurrent` is checked between pages: a call change, stop or access change ends the walk with null.
 */
export async function fetchTranscriptPages(
  api: Pick<ApiClient, 'request'>,
  callId: string,
  signal: AbortSignal,
  stillCurrent: () => boolean = () => true,
): Promise<Transcript | null> {
  const base = `/calls/${encodeURIComponent(callId)}/transcript`;
  let first: Transcript | null = null;
  const segments: Transcript['segments'] = [];
  const seen = new Set<string>();
  let cursor: string | null = null;
  for (let page = 0; page < TRANSCRIPT_MAX_PAGES; page++) {
    const body: DataEnvelope<Transcript> = await api.request<DataEnvelope<Transcript>>(cursor ? `${base}?cursor=${encodeURIComponent(cursor)}` : base, { signal });
    if (signal.aborted || !stillCurrent()) return null;
    const data = body?.data;
    if (!data || !Array.isArray(data.segments) || !Array.isArray(data.gaps)) {
      throw new Error('invalid_transcript_response');
    }
    first ??= data;
    for (const s of data.segments) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      segments.push(s);
    }
    cursor = typeof data.next_cursor === 'string' && data.next_cursor ? data.next_cursor : null;
    if (!cursor || data.state === 'off') break;
  }
  if (!first) return null;
  return { ...first, segments, next_cursor: cursor };
}

export interface LiveTranscriptState {
  transcript: Transcript | null;
  loading: boolean;
  error: string | null;
  errorView?: ErrorView | null;
  initialized?: boolean;
}

export interface LiveTranscriptPollerOptions {
  includeEnded?: boolean;
}

const initialState = (): LiveTranscriptState => ({
  transcript: null,
  loading: false,
  error: null,
  errorView: null,
  initialized: false,
});

export class LiveTranscriptPoller {
  private state: LiveTranscriptState = initialState();
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private controller: AbortController | null = null;
  private inFlightEvents: EventEnvelope[] | null = null;
  private activeFetch: (() => Promise<void>) | null = null;
  private activeCallId: string | null = null;
  private activeEpoch: number | undefined = undefined;
  private generation = 0;
  private unsubscribeAccess: (() => void) | null = null;
  private unsubscribeStream: (() => void) | null = null;

  constructor(
    private readonly api: Pick<ApiClient, 'request'>,
    private readonly access?: MediaAccess,
    private readonly options: LiveTranscriptPollerOptions = {},
  ) {}

  snapshot = (): LiveTranscriptState => this.state;

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  reload = (): void => {
    if (this.activeFetch) {
      this.controller?.abort();
      this.controller = null;
      this.inFlightEvents = null;
      void this.activeFetch();
    }
  };

  private update(next: LiveTranscriptState): void {
    this.state = next;
    for (const fn of [...this.listeners]) fn();
  }

  private detach(): void {
    ++this.generation;
    this.unsubscribeAccess?.();
    this.unsubscribeAccess = null;
    this.unsubscribeStream?.();
    this.unsubscribeStream = null;
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
    this.controller?.abort();
    this.controller = null;
    this.inFlightEvents = null;
    this.activeFetch = null;
  }

  start(call: Pick<Call, 'id' | 'state' | 'transcription_state'>, enabled: boolean): void {
    const accessSnap = this.access?.getSnapshot();
    const accessReady = !accessSnap || accessSnap.state === 'ready';
    const eligibleState = !!this.options.includeEnded || call.state === 'active' || call.state === 'held';
    if (!enabled || !eligibleState || call.transcription_state === 'off' || !accessReady) {
      this.stop();
      return;
    }
    const epoch = accessSnap?.epoch;
    if (this.activeCallId === call.id && this.activeEpoch === epoch) {
      this.detach();
    } else {
      this.stop();
      this.activeCallId = call.id;
      this.activeEpoch = epoch;
    }
    const generation = this.generation;
    this.unsubscribeAccess =
      this.access?.subscribe(() => {
        const currentSnap = this.access?.getSnapshot();
        if (currentSnap?.epoch !== epoch || currentSnap?.state !== 'ready') this.stop();
      }) ?? null;

    const fetchTranscript = async () => {
      if (this.controller) return;
      const controller = new AbortController();
      const inFlightEvents: EventEnvelope[] = [];
      this.controller = controller;
      this.inFlightEvents = inFlightEvents;
      if (!this.state.transcript) {
        this.update({ ...this.state, loading: true, error: null, errorView: null });
      }
      try {
        const data = await fetchTranscriptPages(this.api, call.id, controller.signal, () => {
          if (controller.signal.aborted || generation !== this.generation) return false;
          const currentSnap = this.access?.getSnapshot();
          return !currentSnap || (currentSnap.epoch === epoch && currentSnap.state === 'ready');
        });
        if (controller.signal.aborted || generation !== this.generation) return;
        if (this.access) {
          const currentSnap = this.access.getSnapshot();
          if (currentSnap.epoch !== epoch || currentSnap.state !== 'ready') {
            this.stop();
            return;
          }
        }
        if (!data) return;
        const merged = mergeFetchedTranscript(this.state.transcript, data, inFlightEvents, call.id);
        this.update({ transcript: merged, loading: false, error: null, errorView: null, initialized: true });
      } catch (error) {
        if (controller.signal.aborted || generation !== this.generation || isAbortError(error)) return;
        this.update({
          transcript: this.state.transcript,
          loading: false,
          error: 'Live transcript could not be refreshed. Retrying shortly.',
          errorView: describeError(error, "Couldn't load transcript"),
          initialized: true,
        });
      } finally {
        if (this.controller === controller) {
          this.controller = null;
          this.inFlightEvents = null;
        }
      }
    };
    this.activeFetch = fetchTranscript;

    this.unsubscribeStream = subscribeTranscriptStream(notification => {
      if (generation !== this.generation) return;
      if (notification.kind === 'resync') {
        void fetchTranscript();
        return;
      }
      if (this.inFlightEvents) {
        this.inFlightEvents.push(notification.event);
      }
      const applied = applyTranscriptSseEvent(this.state.transcript, notification.event, call.id);
      if (applied.transcript !== this.state.transcript) {
        this.update({
          transcript: applied.transcript,
          loading: false,
          error: null,
          errorView: null,
          initialized: true,
        });
      }
      if (applied.shouldReload) {
        void fetchTranscript();
      }
    });

    void fetchTranscript();
    if ((call.state === 'active' || call.state === 'held') && call.transcription_state !== 'off') {
      this.timer = setInterval(() => {
        void fetchTranscript();
      }, LIVE_TRANSCRIPT_POLL_MS);
    }
  }

  stop(): void {
    this.detach();
    this.activeCallId = null;
    this.activeEpoch = undefined;
    this.update(initialState());
  }
}
